import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import BrandMark from "../../components/BrandMark";
import Seo from "../../components/Seo";

export default function VerifyEmailPage() {
  const {
    loading,
    isAuthenticated,
    isDevMode,
    emailVerified,
    user,
    firebaseUser,
    isPartner,
    resendVerificationEmail,
    refreshEmailVerification,
    signOut,
  } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [resending, setResending] = useState(false);
  const [checking, setChecking] = useState(false);

  const email = user?.email || firebaseUser?.email || "";
  const nextPath =
    isPartner || user?.role === "partner"
      ? "/partner/onboarding/basics"
      : "/instructor/onboarding/basics";

  useEffect(() => {
    if (!loading && isAuthenticated && (isDevMode || emailVerified)) {
      navigate(nextPath, { replace: true });
    }
  }, [loading, isAuthenticated, isDevMode, emailVerified, navigate, nextPath]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/signup" replace />;
  }

  if (isDevMode || emailVerified) {
    return <Navigate to={nextPath} replace />;
  }

  async function handleResend() {
    setError("");
    setInfo("");
    setResending(true);
    try {
      await resendVerificationEmail();
      setInfo("Verification email sent. Check your inbox and spam folder.");
    } catch (err) {
      setError(err.message || "Could not resend verification email");
    } finally {
      setResending(false);
    }
  }

  async function handleVerified() {
    setError("");
    setInfo("");
    setChecking(true);
    try {
      const ok = await refreshEmailVerification();
      if (ok) {
        navigate(nextPath, { replace: true });
        return;
      }
      setError(
        "Email is not verified yet. Open the link in your inbox, then try again."
      );
    } catch (err) {
      setError(err.message || "Could not check verification status");
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center px-4 py-12">
      <Seo
        title="Verify your email"
        description="Confirm your email to continue onboarding."
        canonicalPath="/verify-email"
        robots="noindex,nofollow"
      />
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <BrandMark />
          <span className="font-bold text-gray-900">
            Yoga Retreats <span className="text-emerald-600">Booking</span>
          </span>
        </Link>
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-8">
          <h1 className="text-2xl font-semibold text-gray-900 mb-1">Verify your email</h1>
          <p className="text-sm text-gray-500 mb-4">
            We sent a verification link to{" "}
            <span className="font-medium text-gray-800">{email || "your email"}</span>. Open it,
            then continue below.
          </p>
          <p className="text-xs text-gray-500 mb-6">
            Can’t find it? Check spam/junk. The link expires after a while — you can resend.
          </p>

          {error && (
            <div className="mb-4 rounded-lg bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>
          )}
          {info && (
            <div className="mb-4 rounded-lg bg-emerald-50 text-emerald-800 text-sm px-3 py-2">
              {info}
            </div>
          )}

          <div className="space-y-3">
            <button
              type="button"
              disabled={checking}
              onClick={handleVerified}
              className="w-full rounded-lg bg-emerald-600 text-white py-2.5 text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
            >
              {checking ? "Checking…" : "I’ve verified — continue"}
            </button>
            <button
              type="button"
              disabled={resending}
              onClick={handleResend}
              className="w-full rounded-lg border border-emerald-200 text-emerald-800 py-2.5 text-sm font-medium hover:bg-emerald-50 disabled:opacity-60"
            >
              {resending ? "Sending…" : "Resend verification email"}
            </button>
          </div>

          <p className="mt-6 text-center text-sm text-gray-500">
            Wrong email?{" "}
            <button
              type="button"
              className="text-emerald-700 font-medium underline"
              onClick={async () => {
                await signOut();
                navigate("/signup", { replace: true });
              }}
            >
              Sign out and start over
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
